import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { receiptService } from "services";
import { ReceiptForm, ReceiptQuery, ReceiptUpdatePayload } from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type ReceiptListKey = ["receipts"] | ["receipts", ReceiptQuery];
type ReceiptDetailKey = ["receipts", "detail", number | string];

const receiptKeys = {
  all: ["receipts"] as const,
  list: (params?: ReceiptQuery): ReceiptListKey =>
    params ? ["receipts", params] : ["receipts"],
  detail: (id: number | string): ReceiptDetailKey => [
    "receipts",
    "detail",
    id,
  ],
};

export function useReceipt() {
  const queryClient = useQueryClient();

  const fetchList = (params?: ReceiptQuery, enabled = true) => {
    return useQuery({
      queryKey: receiptKeys.list(params),
      queryFn: () => receiptService.list(params),
      enabled,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage: data?.totalPage ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: receiptKeys.detail(id),
      queryFn: () => receiptService.show(id),
      enabled: Boolean(id) && enabled,
    });
  };

  const createReceipt = useMutation({
    mutationFn: (payload: ReceiptForm) => receiptService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo phiếu thu thành công");
      }
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo phiếu thu thất bại");
      console.error("createReceipt failed", err);
    },
  });

  const updateReceipt = useMutation({
    mutationFn: (payload: ReceiptUpdatePayload) => receiptService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật phiếu thu thành công");
      }
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật phiếu thu thất bại");
      console.error("updateReceipt failed", err);
    },
  });

  const deleteReceipt = useMutation({
    mutationFn: (id: number | string) => receiptService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá phiếu thu thành công");
      }
      queryClient.invalidateQueries({ queryKey: receiptKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá phiếu thu thất bại");
      console.error("deleteReceipt failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createReceipt,
    updateReceipt,
    deleteReceipt,
  };
}
